/**
 * Staff Management Page
 * List and manage staff members
 */

import { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';
import StaffForm from '../../components/admin/StaffForm';
import ScheduleEditor from '../../components/admin/ScheduleEditor';

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const { data } = await adminAPI.staff.getAll();
      setStaff(data.staff || []);
    } catch (err) {
      console.error('Error fetching staff:', err);
      setError(err.response?.data?.error || 'Failed to load staff members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = () => {
    setEditingStaff(null);
    setShowStaffForm(true);
  };

  const handleEditStaff = (member) => {
    setEditingStaff(member);
    setShowStaffForm(true);
  };

  const handleManageSchedule = (member) => {
    setEditingStaff(member);
    setShowScheduleEditor(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this staff member?')) {
      return;
    }

    try {
      await adminAPI.staff.delete(id);
      fetchStaff();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete staff member');
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingStaff) {
        await adminAPI.staff.update(editingStaff.id, formData);
      } else {
        await adminAPI.staff.create(formData);
      }
      setShowStaffForm(false);
      setEditingStaff(null);
      fetchStaff();
    } catch (err) {
      throw err;
    }
  };

  const handleScheduleSubmit = async (staffId, schedule) => {
    try {
      await adminAPI.staff.updateSchedule(staffId, schedule);
      setShowScheduleEditor(false);
      setEditingStaff(null);
      alert('Schedule updated successfully');
    } catch (err) {
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading staff...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Management</h1>
          <p className="text-gray-600">Manage your team members and their schedules</p>
        </div>
        <button
          onClick={handleAddStaff}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Staff Member
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {staff.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <UserGroupIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No staff members yet</h3>
          <p className="text-gray-500 mb-6">
            Get started by adding your first team member
          </p>
          <button
            onClick={handleAddStaff}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700"
          >
            Add Staff Member
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {staff.map((member) => (
            <StaffCard
              key={member.id}
              member={member}
              onEdit={() => handleEditStaff(member)}
              onDelete={() => handleDelete(member.id)}
              onManageSchedule={() => handleManageSchedule(member)}
            />
          ))}
        </div>
      )}

      {/* Staff Form Modal */}
      {showStaffForm && (
        <StaffForm
          staff={editingStaff}
          onSubmit={handleFormSubmit}
          onClose={() => {
            setShowStaffForm(false);
            setEditingStaff(null);
          }}
        />
      )}

      {/* Schedule Editor Modal */}
      {showScheduleEditor && editingStaff && (
        <ScheduleEditor
          staffId={editingStaff.id}
          staffName={`${editingStaff.firstName} ${editingStaff.lastName}`}
          onSubmit={handleScheduleSubmit}
          onClose={() => {
            setShowScheduleEditor(false);
            setEditingStaff(null);
          }}
        />
      )}
    </div>
  );
}

function StaffCard({ member, onEdit, onDelete, onManageSchedule }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg"
            style={{ backgroundColor: member.color || '#10b981' }}
          >
            {member.firstName[0]}{member.lastName[0]}
          </div>
          <div className="ml-3">
            <h3 className="font-semibold text-gray-900">
              {member.firstName} {member.lastName}
            </h3>
            <p className="text-sm text-gray-500">{member.email}</p>
          </div>
        </div>
        <div className="flex space-x-1">
          <button
            onClick={onEdit}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            title="Edit staff member"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
            title="Delete staff member"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {member.phone && (
        <div className="text-sm text-gray-600 mb-2">
          📞 {member.phone}
        </div>
      )}

      {member.bio && (
        <p className="text-sm text-gray-600 line-clamp-2 mb-4">
          {member.bio}
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          member.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {member.isActive ? 'Active' : 'Inactive'}
        </span>
        <button
          onClick={onManageSchedule}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          Manage Schedule
        </button>
      </div>
    </div>
  );
}

// Import missing icon
function UserGroupIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}
